import { useEffect, useState } from 'react';

/**
 * Responsive hook for media queries
 * @param query CSS media query string
 * @returns boolean indicating if the query matches
 */
function useMediaQuery(query: string): boolean {
    // Resolved during the first render, not in an effect afterwards. Starting at
    // `false` meant every mount briefly reported "not desktop": the shell drew
    // the sidebar expanded, then the effect corrected it, and the width
    // transition animated that correction. Each route mounts its own shell, so
    // the flash happened on every navigation.
    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const media = window.matchMedia(query);
        setMatches(media.matches);
        const listener = (event: MediaQueryListEvent) => setMatches(event.matches);
        media.addEventListener('change', listener);
        return () => media.removeEventListener('change', listener);
        // `matches` was a dependency here, so every change tore the listener down
        // and re-subscribed. The query is the only thing that should do that.
    }, [query]);

    return matches;
}

export default useMediaQuery;
